import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Sparkles, Upload, FileText, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = '.pdf,.docx,.md,.txt';

/**
 * AnalisisIAHipotecaCard
 * ----------------------------------------------------------
 * Card que vive dentro del Paso 1 ("Datos de la Hipoteca") de
 * ReclamacionDetalle. Permite subir la escritura, lanzar el
 * análisis IA, ver el progreso (subida + análisis) y aplicar el
 * resultado a los campos del formulario y a las cláusulas.
 *
 * Props:
 *   - reclamacionId: int
 *   - onDatosExtraidos: callback({ datos_hipoteca }) tras "Aplicar"
 *   - onClausulasAplicadas: callback() tras "Aplicar"
 *   - readOnly: bool (oculta acciones si la reclamación está finalizada)
 */
export default function AnalisisIAHipotecaCard({
  reclamacionId,
  onDatosExtraidos,
  onClausulasAplicadas,
  readOnly = false,
}) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [job, setJob] = useState(null);          // {job_id, estado, progreso, fase, ...}
  const [resultado, setResultado] = useState(null);
  const [aplicando, setAplicando] = useState(false);
  const [expedienteCreado, setExpedienteCreado] = useState(null);  // v77
  const [error, setError] = useState(null);
  const pollerRef = useRef(null);
  const inputRef = useRef(null);

  const token = () => localStorage.getItem('lexduo_token');
  const authHdr = () => ({ Authorization: `Bearer ${token()}` });

  // Cargar último job al montar (para retomar análisis previos)
  useEffect(() => {
    if (!reclamacionId) return;
    axios
      .get(`${API}/api/extrajudicial/reclamaciones/${reclamacionId}/analisis-ia/last`, { headers: authHdr() })
      .then((res) => {
        if (res.data && res.data.job_id) {
          setJob(res.data);
          if (res.data.estado === 'completado') {
            loadResultado(res.data.job_id);
          } else if (['subiendo', 'convirtiendo', 'analizando'].includes(res.data.estado)) {
            startPoller(res.data.job_id);
          }
        }
      })
      .catch(() => {});
    return () => stopPoller();
    // eslint-disable-next-line
  }, [reclamacionId]);

  const stopPoller = () => {
    if (pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  };

  const startPoller = (jobId) => {
    stopPoller();
    pollerRef.current = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/analisis-ia/jobs/${jobId}/progress`,
          { headers: authHdr() }
        );
        setJob(res.data);
        if (res.data.estado === 'completado') {
          stopPoller();
          await loadResultado(jobId);
        } else if (res.data.estado === 'error') {
          stopPoller();
          setError(res.data.error_mensaje || 'Error desconocido');
          toast.error('El análisis IA falló');
        }
      } catch (e) {
        stopPoller();
        setError('No se pudo consultar el progreso del análisis');
      }
    }, 2000);
  };

  const loadResultado = async (jobId) => {
    try {
      const res = await axios.get(
        `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/analisis-ia/jobs/${jobId}/result`,
        { headers: authHdr() }
      );
      setResultado(res.data);
    } catch (e) {
      setError('No se pudo obtener el resultado del análisis');
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecciona un archivo primero');
      return;
    }
    setUploading(true);
    setUploadPct(0);
    setError(null);
    setResultado(null);
    setJob(null);

    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(
        `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/analisis-ia/upload`,
        form,
        {
          headers: { ...authHdr(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round((evt.loaded / evt.total) * 100);
              setUploadPct(pct);
            }
          },
        }
      );
      toast.success('Archivo subido. Iniciando análisis IA…');
      const jid = res.data.job_id;
      setJob({ job_id: jid, estado: 'subiendo', progreso: 10, fase: 'Subida completada' });
      startPoller(jid);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Error subiendo el archivo';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const handleAplicar = async () => {
    if (!job?.job_id) return;
    setAplicando(true);
    try {
      const res = await axios.post(
        `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/analisis-ia/jobs/${job.job_id}/aplicar`,
        {
          aplicar_datos_hipoteca: true,
          aplicar_clausulas_predefinidas: true,
          aplicar_clausulas_personalizadas: true,
        },
        { headers: authHdr() }
      );
      const ap = res.data.aplicados || {};
      if (res.data.expediente && res.data.expediente.numero_expediente) {
        setExpedienteCreado(res.data.expediente);
        toast.success(`Expediente principal ${res.data.expediente.ya_existia ? 'vinculado' : 'creado'}: ${res.data.expediente.numero_expediente}`);
      }
      toast.success(
        `Aplicado: ${ap.datos_hipoteca?.length || 0} datos, ${ap.predefinidas?.length || 0} cláusulas predefinidas, ${ap.personalizadas || 0} personalizadas`
      );
      if (onDatosExtraidos) onDatosExtraidos(resultado);
      if (onClausulasAplicadas) onClausulasAplicadas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error aplicando el resultado');
    } finally {
      setAplicando(false);
    }
  };

  const handleReanalizar = () => {
    setJob(null);
    setResultado(null);
    setError(null);
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ----- Render -----
  const isWorking = uploading || (job && ['subiendo', 'convirtiendo', 'analizando'].includes(job.estado));
  const globalPct = uploading
    ? Math.min(5 + Math.round(uploadPct * 0.10), 15)   // 5-15% durante upload
    : (job?.progreso ?? 0);

  return (
    <Card className="border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-white" data-testid="analisis-ia-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          Análisis IA de la escritura hipotecaria
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Sube la escritura (PDF/DOCX) y la IA extraerá los datos de la hipoteca y detectará las cláusulas
          potencialmente abusivas. Podrás revisar y aplicar el resultado al formulario.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* v77: expediente vinculado */}
        {expedienteCreado?.numero_expediente && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-300" data-testid="expediente-creado-badge">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="flex-1">
              <div className="text-sm font-medium text-emerald-900">Expediente principal vinculado</div>
              <div className="text-xs text-emerald-700">{expedienteCreado.titulo}</div>
            </div>
            <a
              href={`/expedientes/${expedienteCreado.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              data-testid="abrir-expediente-link"
            >
              {expedienteCreado.numero_expediente}
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        )}


        {/* Estado: sin job activo y sin resultado → uploader */}
        {!isWorking && !resultado && !error && (
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={handleFile}
              disabled={readOnly}
              className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200 cursor-pointer"
              data-testid="analisis-ia-file-input"
            />
            <Button
              onClick={handleUpload}
              disabled={!file || readOnly}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="analisis-ia-launch-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Analizar con IA
            </Button>
          </div>
        )}

        {/* Estado: trabajando → barra de progreso */}
        {isWorking && (
          <div className="space-y-3" data-testid="analisis-ia-progress">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-800">
              <Loader2 className="w-4 h-4 animate-spin" />
              {uploading
                ? `Subiendo archivo… ${uploadPct}%`
                : (job?.fase || 'Procesando…')}
            </div>
            <Progress value={globalPct} className="h-3" />
            <p className="text-xs text-slate-500">
              {globalPct}% — El análisis puede tardar 30-90 segundos según el tamaño del documento.
            </p>
          </div>
        )}

        {/* Estado: error */}
        {error && !isWorking && (
          <Alert variant="destructive" data-testid="analisis-ia-error">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              <strong>El análisis falló:</strong> {error}
              <Button variant="link" size="sm" onClick={handleReanalizar} className="ml-2 p-0 h-auto">
                Probar de nuevo
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Estado: completado → resumen + aplicar */}
        {resultado && !isWorking && (
          <div className="space-y-3" data-testid="analisis-ia-result">
            <Alert className="bg-green-50 border-green-300">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <strong>Análisis completado.</strong> Revisa los datos extraídos antes de aplicarlos.
              </AlertDescription>
            </Alert>

            {/* Datos extraídos */}
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">📋 DATOS DE LA HIPOTECA</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {resultado.datos_hipoteca?.fecha_firma && (
                  <div><span className="text-slate-500">Fecha firma:</span> <strong>{resultado.datos_hipoteca.fecha_firma}</strong></div>
                )}
                {resultado.datos_hipoteca?.entidad_prestamista && (
                  <div><span className="text-slate-500">Entidad:</span> <strong>{resultado.datos_hipoteca.entidad_prestamista}</strong></div>
                )}
                {resultado.datos_hipoteca?.notario && (
                  <div><span className="text-slate-500">Notario:</span> <strong>{resultado.datos_hipoteca.notario}</strong></div>
                )}
                {resultado.datos_hipoteca?.protocolo && (
                  <div><span className="text-slate-500">Protocolo:</span> <strong>{resultado.datos_hipoteca.protocolo}</strong></div>
                )}
                {resultado.datos_hipoteca?.capital_eur != null && (
                  <div><span className="text-slate-500">Capital:</span> <strong>{Number(resultado.datos_hipoteca.capital_eur).toLocaleString('es-ES')} €</strong></div>
                )}
                {resultado.datos_hipoteca?.plazo_anios != null && (
                  <div><span className="text-slate-500">Plazo:</span> <strong>{resultado.datos_hipoteca.plazo_anios} años</strong></div>
                )}
                {resultado.datos_hipoteca?.tipo_interes && (
                  <div><span className="text-slate-500">Tipo interés:</span> <strong>{resultado.datos_hipoteca.tipo_interes}</strong></div>
                )}
                {resultado.datos_hipoteca?.indice_referencia && (
                  <div><span className="text-slate-500">Índice:</span> <strong>{resultado.datos_hipoteca.indice_referencia}</strong></div>
                )}
              </div>
            </div>

            {/* Cláusulas detectadas */}
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">⚖️ CLÁUSULAS DETECTADAS</div>
              {(resultado.clausulas_detectadas || []).length === 0 ? (
                <p className="text-sm text-slate-500 italic">La IA no detectó cláusulas potencialmente abusivas.</p>
              ) : (
                <div className="space-y-2">
                  {resultado.clausulas_detectadas.map((cl, idx) => {
                    const enPredefinidas = Boolean(resultado._mapping?.predefinidas && Object.keys(resultado._mapping.predefinidas).some(k =>
                      (cl.nombre || '').toLowerCase().includes(k.replace('clausula_', '').replace('_', ' '))
                    ));
                    return (
                      <div key={idx} className="flex items-start gap-2 text-sm border-l-4 border-purple-300 pl-3 py-1">
                        <Badge variant={cl.viabilidad_pct >= 70 ? 'default' : 'secondary'} className="shrink-0">
                          {cl.codigo}
                        </Badge>
                        <div className="flex-1">
                          <div className="font-medium">{cl.nombre}</div>
                          {cl.descripcion_breve && <div className="text-xs text-slate-600">{cl.descripcion_breve}</div>}
                          <div className="text-xs text-slate-500 mt-0.5">
                            {cl.viabilidad_pct != null && <span>Viabilidad: <strong>{cl.viabilidad_pct}%</strong></span>}
                            {cl.impacto_eur != null && cl.impacto_eur > 0 && (
                              <span className="ml-2">Impacto: <strong>{Number(cl.impacto_eur).toLocaleString('es-ES')} €</strong></span>
                            )}
                            {enPredefinidas ? (
                              <Badge variant="outline" className="ml-2 text-xs">Predefinida</Badge>
                            ) : (
                              <Badge variant="outline" className="ml-2 text-xs bg-amber-50 border-amber-300">Se añadirá como personalizada</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleReanalizar} data-testid="analisis-ia-reanalizar-btn">
                <FileText className="w-4 h-4 mr-2" />
                Subir otro
              </Button>
              <Button
                onClick={handleAplicar}
                disabled={aplicando || readOnly}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="analisis-ia-aplicar-btn"
              >
                {aplicando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Aplicar al formulario
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
